import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { BookOpenIcon, PaletteIcon, PenToolIcon, MessageSquareTextIcon, GlobeIcon, ScissorsIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Container } from '../../components/ui/Container';
import { Button } from '../../components/ui/Button';
import { SubpageHero } from '../../components/sections/subpages/SubpageHero';
import { PatternDecor } from '../../components/ui/PatternDecor';
const courses = [{
  icon: BookOpenIcon,
  title: 'Arabic Language: Everyday Communication & Quranic Understanding',
  body: 'Focus on comprehension — from everyday Arabic to understanding the Quran you recite. For tajweed and memorization, we are glad to point you to dedicated Quran schools.'
}, {
  icon: MessageSquareTextIcon,
  title: 'English for Confidence & Public Life',
  body: 'For clear communication in work and daily life. Practical speaking and confidence building.'
}, {
  icon: GlobeIcon,
  title: 'European Languages (Integration Focus)',
  body: 'French / German / Spanish tracks designed for everyday integration and social confidence.'
}, {
  icon: PenToolIcon,
  title: 'Arabic Calligraphy',
  body: 'Culture meets creativity. A calm, structured craft with cultural depth and aesthetic discipline, including proper adab of sacred calligraphy.'
}, {
  icon: PaletteIcon,
  title: 'Islamic Arts & Crafts',
  body: 'Calligraphy, ornament, and traditional patterns — creative practice with cultural literacy, including proper adab of sacred calligraphy.'
}, {
  icon: ScissorsIcon,
  title: 'Modest Fashion & Clothing Design',
  body: 'Design and sewing of modest clothing. Includes practical skills with potential business application.'
}];
export function CreativeLearning() {
  return <>
      <SubpageHero eyebrow="Language, culture & skills — heritage as strength" title="Creative Learning" paragraphs={['Creative Learning includes language, culture, and practical skills for Muslim adults living in non-Muslim countries.', 'These courses are designed for confidence, integration, and self-expression — enabling active social participation and skill acquisition while fully maintaining your heritage.']} primaryCta={{
      label: 'Explore Catalog',
      to: '#catalog'
    }} secondaryCta={{
      label: 'Request Info',
      to: '/en/contact'
    }} image="https://cdn.magicpatterns.com/uploads/ninMqHXNBrWb99GzWxD16c/Creative2.jpg" imageAlt="Muslim woman sketching a garment pattern at her desk beside Islamic arts books, calligraphy pens, and a laptop showing geometric designs" compact masked data-id="element-1979" data-mp="19tptsvvobs0h" />

      {/* Language, Culture & Skills */}
      <section id="catalog" className="py-16 md:py-24 scroll-mt-24 bg-sage-50/60" data-id="element-1980" data-mp="acn1jgrxux2c">
        <Container data-id="element-1981" data-mp="fbrcocyfb4vb">
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-1982" data-mp="7u1gu2k6v34c">
            Language, Culture & Skills
          </h2>
          <p className="mt-5 text-ink-700 leading-relaxed" data-id="element-1983" data-mp="2wji14zvy4a2">
            This direction combines cultural literacy, creativity, and everyday
            skills. It helps adults feel grounded, confident, and capable — in
            family life, public life, and community life.
          </p>

          <div className="mt-10 grid md:grid-cols-2 gap-5" data-id="element-1984" data-mp="3l1wful2a71jv">
            {courses.map((c, i) => <motion.div key={c.title} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.5,
            delay: i * 0.06
          }} className={`rounded-2xl border border-ink-900/5 p-6 shadow-soft ${['bg-sage-100', 'bg-[#F7EDE9]', 'bg-sand-100', 'bg-[#EDF2F7]', 'bg-sage-100', 'bg-[#F7EDE9]'][i]}`} data-id="element-1985" data-mp="3g93bhyaq5v8b" data-mpi={i}>
                <span className="w-11 h-11 inline-flex items-center justify-center rounded-xl bg-white border border-forest-700/10 text-forest-700" data-id="element-1986" data-mp="3dae9o7rcescu">
                  <c.icon size={18} strokeWidth={1.6} data-id="element-1987" data-mp="3h93nu2k7gdxj" />
                </span>
                <h3 className="mt-5 font-display text-lg text-ink-900" data-id="element-1988" data-mp="339u791s37ht1">
                  {c.title}
                </h3>
                <p className="mt-3 text-ink-700 text-sm leading-relaxed" data-id="element-1989" data-mp="388f8n4j2s7j2">
                  {c.body}
                </p>
              </motion.div>)}
          </div>

          <div className="mt-8 flex flex-col gap-2 rounded-2xl border border-sage-200 bg-white px-6 py-5 sm:flex-row sm:items-center" data-id="element-1990" data-mp="2ycu7t9pqtdaw">
            <span className="inline-flex w-fit whitespace-nowrap rounded-full bg-sage-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700" data-id="element-1991" data-mp="wkz0f6airuyh">
              Coming soon
            </span>
            <p className="flex-1 text-center text-sm font-semibold text-ink-700" data-id="element-1992" data-mp="2d1y2xiv4ca44">
              The names of our confirmed teachers, educators, and Shariah Board
              members will be announced soon.
            </p>
          </div>
        </Container>
      </section>

      {/* Learning Formats */}
      <section className="relative py-16 md:py-24 overflow-hidden isolate bg-white/60" data-id="element-1993" data-mp="30qh8vrqp4at1">
        <PatternDecor side="left" y="top" size="lg" data-id="element-1994" data-mp="2vs5z1h0ei0at" />
        <Container data-id="element-1995" data-mp="2vrmjg6y83nzy">
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-1996" data-mp="2vu8nvxpthndy">
            Learning Formats & Pricing
          </h2>
          <div className="mt-10 grid md:grid-cols-3 gap-5" data-id="element-1997" data-mp="34sc8mrszag4p">
            {[{
            title: 'Language Mastery',
            items: ['Programs: Arabic Language: Everyday Communication & Quranic Understanding, English for Confidence & Public Life, European Languages (Integration Focus)', 'Intensity: 2 live sessions per week', 'Format: Continuous training', 'Price: €95 / month']
          }, {
            title: 'Traditional Arts',
            items: ['Programs: Arabic Calligraphy, Islamic Arts & Crafts', 'Intensity: 1 live session per week', 'Format: Continuous training', 'Price: €60 / month']
          }, {
            title: 'Applied Design',
            items: ['Program: Modest Fashion & Clothing Design', 'Intensity: 1 live session per week', 'Format: Continuous training', 'Price: €65 / month']
          }].map((format, i) => <motion.div key={format.title} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.5,
            delay: i * 0.06
          }} className={`rounded-2xl border border-ink-900/5 p-6 shadow-soft ${['bg-sage-100', 'bg-[#F7EDE9]', 'bg-sand-100'][i]}`} data-id="element-1998" data-mp="l0n19ggo1dfh" data-mpi={i}>
                <h3 className="font-display text-lg text-ink-900" data-id="element-1999" data-mp="2fg3kv7wo06u2">
                  {format.title}
                </h3>
                <ul className="mt-4 space-y-2.5" data-id="element-2000" data-mp="2ahil19lbrp65">
                  {format.items.map(item => <li key={item} className="flex items-start gap-2.5 text-ink-700 text-sm leading-relaxed" data-id="element-2001" data-mp="37wwgcvpg35kq">
                      <span className="w-1.5 h-1.5 rounded-full bg-forest-700 mt-2 flex-shrink-0" data-id="element-2002" data-mp="2bi39wmgw8lfe" />
                      <span data-id="element-2003" data-mp="2g8te3wdl2i8i">{item}</span>
                    </li>)}
                </ul>
              </motion.div>)}
          </div>
        </Container>
      </section>

      {/* Why this matters */}
      <section className="relative py-16 md:py-24 overflow-hidden isolate bg-[#EDF2F7]/60" data-id="element-2004" data-mp="26i8i8gpanrta">
        <PatternDecor side="right" y="middle" size="md" data-id="element-2005" data-mp="2u82vgzdizie2" />
        <Container data-id="element-2006" data-mp="2u6hx0yo3bfax">
          <div className="grid lg:grid-cols-12 gap-12 items-center" data-id="element-2007" data-mp="2z7npqwhr32kg">
            <div className="lg:col-span-6" data-id="element-2008" data-mp="2z6w52gpoyp8y">
              <h2 className="font-display text-3xl md:text-4xl text-ink-900 leading-tight" data-id="element-2009" data-mp="2l8frq6xbq0hu">
                Build confidence through culture
              </h2>
              <p className="mt-5 text-ink-700 leading-relaxed" data-id="element-2010" data-mp="2pbkpxxadfg0o">
                Creative learning goes beyond skill-building. It helps Muslim
                adults engage with their traditions in a way that strengthens
                daily life — at home, at work, and in the community.
              </p>
              <p className="mt-4 text-ink-700 leading-relaxed" data-id="element-2011" data-mp="16cmudewfqmbv">
                Every course is practical and grounded. You gain tools you will actually use.
              </p>
            </div>
            <div className="lg:col-span-6" data-id="element-2012" data-mp="1g6wwhob6iyzs">
              <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-card" data-id="element-2013" data-mp="1b9n01opdhn2g">
                <img src="https://cdn.magicpatterns.com/uploads/1SkvqfxozFzGVZAoCy8He8/Arts2.jpg" alt="Muslim woman practising Islamic calligraphy with ink and a brush at a desk beside a laptop and art books" className="w-full h-full object-cover" data-id="element-2014" data-mp="njspjdsj0ald" />
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Community Support & Access */}
      <section className="py-16 md:py-24 bg-sage-50/60" data-id="element-2015" data-mp="4s9kl1exymgp">
        <Container data-id="element-2016" data-mp="3w0njg3rciknb">
          <h3 className="font-display text-lg text-ink-900" data-id="element-2017" data-mp="3vz2km1y00fjn">
            Community Support & Access
          </h3>
          <p className="mt-4 text-ink-700 leading-relaxed" data-id="element-2018" data-mp="1c44e82f8esr0">
            Cultural and creative programs promote heritage appreciation,
            self-expression, and community skills, contributing to social
            inclusion and well-being. Some cohorts may include limited
            scholarship places through community partnerships.
          </p>
        </Container>
      </section>

      {/* CTA */}
      <section className="relative py-16 md:py-24 overflow-hidden isolate bg-white/60" data-id="element-2019" data-mp="t7382e9xymlh">
        <PatternDecor side="left" y="bottom" size="md" data-id="element-2020" data-mp="odij1bqbt2yf" />
        <Container data-id="element-2021" data-mp="kcyjk1ia5n94">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8" data-id="element-2022" data-mp="kejdpq0on46g">
            <div className="max-w-xl" data-id="element-2023" data-mp="ff6k8mfb2yuw">
              <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-2024" data-mp="fcaj6p85vu28">
                Start learning in a creative cohort
              </h2>
              <p className="mt-4 text-ink-700" data-id="element-2025" data-mp="5h8vzyxg522a">
                Request the upcoming cohort list and choose a course that fits
                your goals and schedule.
              </p>
            </div>
            <div className="flex flex-wrap gap-3" data-id="element-2026" data-mp="ac26rmwp20z">
              <Link to="/en/waitlist" data-id="element-2027" data-mp="5ago4x6l2oyl">
                <Button variant="primary" data-id="element-2028" data-mp="5bhyt0jot9fs">Join</Button>
              </Link>
              <Link to="/en/contact" data-id="element-2029" data-mp="a99tg2bpv9no">
                <Button variant="secondary" data-id="element-2030" data-mp="a88bddwa9mwg">Request Info</Button>
              </Link>
            </div>
          </div>
        </Container>
      </section>
    </>;
}